import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Spin, Button, Space } from 'antd';
import {
  ProductOutlined,
  TagsOutlined,
  SafetyOutlined,
  TeamOutlined,
  PlusCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { Entitlement, OrgEntitlement, Product, SKU } from '../../types';
import { useFieldConfig } from '../../contexts/FieldConfigContext';
import './Dashboard.css';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isNavDisabled } = useFieldConfig();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [skus, setSKUs] = useState<SKU[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [orgEntitlements, setOrgEntitlements] = useState<OrgEntitlement[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [productsData, skusData, entitlementsData, orgEntitlementsData] = await Promise.all([
        apiService.getProducts(),
        apiService.getSKUs(),
        apiService.getEntitlements(),
        apiService.getOrgEntitlements(),
      ]);
      setProducts(productsData);
      setSKUs(skusData);
      setEntitlements(entitlementsData);
      setOrgEntitlements(orgEntitlementsData);
    } catch {
      // Failed to load dashboard data
    } finally {
      setLoading(false);
    }
  };

  const sortByUpdatedAt = <T extends { updated_at?: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => {
      const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return bTime - aTime;
    });

  const productColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '—',
    },
  ];

  const skuColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'SKU Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '—',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>Product Onboarding Dashboard</Title>
        <Space>
          {!isNavDisabled('onboard') && (
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => navigate('/onboard')}
              size="large"
            >
              Onboard New
            </Button>
          )}
          {!isNavDisabled('update') && (
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => navigate('/update')}
              size="large"
            >
              Update Entities
            </Button>
          )}
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Products"
              value={products.length}
              prefix={<ProductOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total SKUs"
              value={skus.length}
              prefix={<TagsOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Entitlements"
              value={entitlements.length}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Org Entitlements"
              value={orgEntitlements.length}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Recent Products" bordered={false}>
            <Table
              dataSource={sortByUpdatedAt(products)}
              columns={productColumns}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Recent SKUs" bordered={false}>
            <Table
              dataSource={sortByUpdatedAt(skus)}
              columns={skuColumns}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
